import { useTranslation } from 'react-i18next'
import BupSpinner from '@BUP/components/Loading/Spinner'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import { multiCourse, STUDENT } from '@BUP/constants'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useGetUsersByCourseIds } from '@BUP/hooks/Enrollments/useGetUsersByCourseIds'
import { useSociogramActions } from '@BUP/hooks/Sociogram/useSociogramContext'
import { useSendActivationSurvey } from '@BUP/hooks/Sociogram/useSendActivationSurvey'
import { findNivel } from '@BUP/services/BraveUp'
import { LocationContext } from '@BUP/contexts/LocationContext'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import ModalComponent from '../Modal'
import styles from './update-survey-button.module.scss'
import AlertComponent from '../Alert'
import { PoweroffOutlined } from '@ant-design/icons'

const UpdateSurveyButton = ({
  survey,
  size = 'medium'
}) => {
  const i18n = useTranslation()
  const locale = useContext(LocationContext)
  const lang = locale && locale.locale

  const { isActive, isDone, courses = [], sociogram_id: surveySociogram } = survey || {}

  const notActivated = !isActive && !isDone
  const isActivated = isActive && !isDone
  const isClosed = !isActive && isDone

  const [stateSurveyModal, setStateSurveyModal] = useState(false)
  const { setDataResponseActiveSurvey } = useSociogramActions()
  const organization = useContext(OrganizationContext)

  const { type: organizationType } = organization
  const isMultiCourse = organizationType === multiCourse

  const {
    updateSurveySociogram,
    isLoading: loadingResponseSurvey,
    dataResponseSurvey
  } = useSendActivationSurvey(survey, surveySociogram.id)

  useEffect(() => {
    if (dataResponseSurvey) {
      setStateSurveyModal(false)
      setDataResponseActiveSurvey(dataResponseSurvey)
    }
  }, [dataResponseSurvey, setDataResponseActiveSurvey])

  const handleShowSurveyModal = () => {
    setStateSurveyModal(true)
  }

  const handleCloseSurveyModal = () => {
    setStateSurveyModal(false)
  }

  const handleConfirmSurvey = async (data, action) => {
    await updateSurveySociogram({
      data, action
    })
  }

  const coursesId = useMemo(() => {
    return courses.map(elem => elem?.id)
  }, [courses])

  const { data: usersList, loading: isLoadingUsers } = useGetUsersByCourseIds(coursesId, STUDENT)
  const totalCountUsers = useMemo(() => {
    return usersList.reduce((acc, item) => acc + (item.count || 0), 0)
  }, [usersList])

  const course = courses[0]
  const nivel = lang ? findNivel(lang, course) : null
  const splitNivel = nivel && nivel.id.split(':')[1]
  const surveyName = isMultiCourse ? i18n.t(`common.resources.course.values.tags.nivel.${splitNivel}`) : course.name

  const action = notActivated ? 'active' : isActivated ? 'finish' : null
  const canUpdateSurvey = !isClosed && Boolean(action)
  const actionVerb = action ? i18n.t(`pages.data.sociogram.activeSociogramEce.updateSurveyButton.action.${action}`) : ''
  const buttonText = canUpdateSurvey
    ? i18n.t(`pages.data.sociogram.activeSociogramEce.updateSurveyButton.button.${action}`)
    : i18n.t('pages.data.sociogram.activeSociogramEce.updateSurveyButton.button.closed')

  if (isLoadingUsers) return <BupSpinner size='small' />
  if (totalCountUsers === 0) {
    return <p>{i18n.t('pages.data.sociogram.activeSociogramEce.table.emptyCourseText')}</p>
  }

  return (
    <div>
      <ButtonComponent
        buttonText={buttonText}
        colorType='tertiary'
        bold={false}
        disabled={surveySociogram.isDone || !canUpdateSurvey}
        onClick={handleShowSurveyModal}
        icon={<PoweroffOutlined />}
        size={size}

      />
      {canUpdateSurvey && (
        <ModalComponent
          stateModal={stateSurveyModal}
          onClickCloseModal={() => {
            handleCloseSurveyModal()
          }}
          modalTitle={<>{i18n.t('pages.data.sociogram.activeSociogramEce.updateSurveyButton.modal.title', { action: actionVerb })} <span className={styles.survey_name}>{surveyName}</span></>}
          modalDescription={
            <div className={styles.modal_description_wrapper}>
              {action === 'active' && (
                <AlertComponent message={i18n.t('pages.data.sociogram.activeSociogramEce.activationSurveyModal.alertMessage')} type='info' />
              )}
              {i18n.t('pages.data.sociogram.activeSociogramEce.updateSurveyButton.modal.description', { action: actionVerb })}
            </div>
          }
          onClickConfirm={() => handleConfirmSurvey(
            action === 'active'
              ? {
                  isActive: true,
                  isDone: false
                }
              : {
                  isDone: true,
                  isActive: false
                },
            action
          )}
          loading={loadingResponseSurvey}
          dataTest={`${action}-survey`}
        />
      )}
    </div>
  )
}

export default UpdateSurveyButton
